import { useEffect } from 'react';
import { Link } from 'react-router';
import { APP_NAME } from '@sellit/shared';
import { PoweredBy, Wordmark } from '../lib/Brand';
import { setPageTitle } from '../lib/format';
import { Icon, type IconName } from '../lib/Icon';
import { StartSellingButton } from './StartSellingPage';


const STEPS: { icon: IconName; title: string; text: string }[] = [
  { icon: 'camera', title: 'Snap a photo', text: 'Take a picture or upload one from your phone.' },
  { icon: 'tag', title: 'Set your price', text: 'Enter an amount, choose Free, or accept offers.' },
  { icon: 'share', title: 'Post and share', text: 'Your item appears on your sale page. Share one link with everyone.' },
];

// A made-up sale, drawn with local images, so visitors can see the result before asking for an account.
const DEMO_ITEMS = [
  { number: 1000, title: 'Cordless power drill', price: '$35', img: '/demo/drill.webp', status: 'Available' },
  { number: 1001, title: 'Wood side table', price: '$20', img: '/demo/table.webp', status: 'Available' },
  { number: 1002, title: 'Office chair', price: '$40', img: '/demo/chair.webp', status: 'Pending' },
];

const BENEFITS: { icon: IconName; title: string; text: string }[] = [
  { icon: 'check', title: 'Completely free', text: 'No listing fees, commissions, or subscriptions.' },
  { icon: 'user', title: 'No buyer accounts', text: 'Buyers can contact sellers without signing up.' },
  { icon: 'phone', title: 'Easy contact', text: 'Interested buyers can reach sellers through the available contact options.' },
  { icon: 'pin', title: 'Your location stays general', text: 'Share your city or neighborhood without publishing your street address.' },
];

// Public landing page at "/". Claims about data here must match what the code does (see PrivacyPage).
export function HomePage() {
  useEffect(() => setPageTitle(`${APP_NAME}: snap it, price it, share it`), []);
  return (
    <>
      <main className="landing">
        <section className="landing-hero">
          <Wordmark className="wordmark" />
          <p className="landing-kicker">Free · Works on any phone · Nothing to install</p>
          <h1>Snap it. Price it. Share it.</h1>
          <p className="landing-lede">
            Turn your stuff into a sale page in minutes. Snap photos, set prices, and share one link. No fees, no apps, no hassle.
          </p>
          <div className="landing-cta">
            <StartSellingButton />
            <Link className="link" to="/admin">Seller log in</Link>
          </div>
        </section>

        <section className="landing-section" aria-labelledby="how">
          <h2 id="how">Selling made simple</h2>
          <ol className="landing-steps">
            {STEPS.map((s, i) => (
              <li key={s.title}>
                <span className="step-num" aria-hidden="true">{i + 1}</span>
                <span className="step-body">
                  <strong><Icon name={s.icon} size={18} /> {s.title}</strong>
                  <span className="muted">{s.text}</span>
                </span>
              </li>
            ))}
          </ol>
        </section>

        <section className="landing-section" aria-labelledby="example">
          <h2 id="example">See what your sale could look like</h2>
          <figure className="demo-sale">
            <div className="demo-head">
              <span className="demo-badge">Example</span>
              <strong>Garcia Family Moving Sale</strong>
              <span className="muted"><Icon name="pin" size={14} /> Heights, Houston · 3 items</span>
            </div>
            <ul className="demo-grid">
              {DEMO_ITEMS.map((item) => (
                <li key={item.number}>
                  <div className="tile-img">
                    <img src={item.img} alt={item.title} width={400} height={400} loading="lazy" />
                    {item.status !== 'Available' && <span className="ribbon pending">{item.status}</span>}
                  </div>
                  <div className="tile-text">
                    <span className="price">{item.price}</span>
                    <span className="title"><span className="num">#{item.number}</span> {item.title}</span>
                  </div>
                </li>
              ))}
            </ul>
            <figcaption className="muted small">A sample sale for illustration. Your page shows your own photos and prices.</figcaption>
          </figure>
        </section>

        <section className="landing-section" aria-labelledby="why">
          <h2 id="why">Made to be easy</h2>
          <ul className="landing-features">
            {BENEFITS.map((f) => (
              <li key={f.title}>
                <span className="feature-icon"><Icon name={f.icon} size={20} /></span>
                <span>
                  <strong>{f.title}</strong>
                  <span className="muted">{f.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-section landing-privacy" aria-labelledby="data">
          <h2 id="data"><Icon name="shield" size={20} /> Your privacy matters</h2>
          <ul>
            <li>We don't sell your personal information.</li>
            <li>Your street address stays off your public sale page.</li>
            <li>Buyer contact details go only to the relevant seller.</li>
          </ul>
          <Link to="/privacy">Read our Privacy Policy</Link>
        </section>

        <section className="landing-final" aria-labelledby="ready">
          <h2 id="ready">Ready to sell?</h2>
          <p className="muted">Your next sale starts with a photo.</p>
          <StartSellingButton />
        </section>
      </main>
      <PoweredBy showLogin />
    </>
  );
}
